/**
 * Hilfeseite /hilfe (PV-037). Rendert die sichtbaren Themen aus HILFE_THEMEN der Reihe nach, jedes
 * als Abschnitt mit Sprungmarke (A1). Eine Quelle, zwei Zugänge: dieselben Komponenten zeigt der
 * ⓘ-Knopf an Phase Board und Mission Control.
 *
 * Beim Öffnen wird „News gelesen bis" auf jetzt gesetzt (A11) — der beim Öffnen gültige Wert wird
 * vorher für den Besuch eingefroren (NewsBesuchContext), damit die neuen News oben bleiben (A12).
 */
import type { NewsListeResponse } from '@projektverwaltung/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { HILFE_THEMEN } from '../../components/v2/hilfe/hilfe-themen.js';
import { api } from '../../lib/api.js';
import { useFeatureFlags } from '../../lib/features.js';
import { NEWS_QUERY_KEY, type NewsBesuch, NewsBesuchContext, useNews } from '../../lib/news.js';

export function HilfePage() {
  const { t } = useTranslation();
  const { flags } = useFeatureFlags();
  const location = useLocation();
  const queryClient = useQueryClient();
  const news = useNews({ frischBeimOeffnen: true });
  const [besuchGelesenBis, setBesuchGelesenBis] = useState<string | null>(null);
  const besuch = useMemo<NewsBesuch>(
    () =>
      besuchGelesenBis === null
        ? { status: 'wartet' }
        : { status: 'aktiv', gelesenBis: besuchGelesenBis },
    [besuchGelesenBis],
  );
  const gemeldet = useRef(false);
  const themen = HILFE_THEMEN.filter((th) => th.sichtbar(flags));
  const anzahl = themen.length;

  // Erster für diesen Besuch FRISCHE Datenstand (nicht der Cache der Kopfleiste, der bis zu fünf
  // Minuten alt sein kann): einfrieren, dann serverseitig „gelesen" melden und den Cache
  // nachziehen — die Zählmarke in der Kopfleiste verschwindet sofort (A11). Die Ref hält den
  // Aufruf auch bei doppelten Effekten (StrictMode) auf genau einen je Besuch.
  useEffect(() => {
    // Nur nach einem ERFOLGREICHEN frischen Abruf dieses Besuchs: `isFetchedAfterMount` ist auch
    // nach einem fehlgeschlagenen Abruf wahr, `news.data` dann noch der alte Cache; `isSuccess`
    // ist es nur, wenn der letzte Abruf gelang.
    if (!news.isSuccess || !news.isFetchedAfterMount || gemeldet.current) return;
    gemeldet.current = true;
    setBesuchGelesenBis(news.data.gelesen_bis);
    void api.markNewsGelesen().then(
      async (antwort) => {
        try {
          // Eine noch laufende Abfrage brächte den Stand von VOR dem POST und ließe die Marke
          // wiederkehren: erst abbrechen, dann schreiben.
          await queryClient.cancelQueries({ queryKey: NEWS_QUERY_KEY });
          queryClient.setQueryData<NewsListeResponse>(NEWS_QUERY_KEY, (alt) =>
            alt ? { ...alt, gelesen_bis: antwort.gelesen_bis } : alt,
          );
        } catch {
          // Cache-Nachzug ist nur Komfort: die Marke verschwindet spätestens beim nächsten Abruf.
        }
      },
      // Fehlgeschlagen: die Marke bleibt stehen, der nächste Besuch meldet erneut.
      () => undefined,
    );
  }, [news.isSuccess, news.data, news.isFetchedAfterMount, queryClient]);

  // Sprung zum Anker (/hilfe#rolle): erst, wenn der News-Abschnitt nicht mehr „Lädt…" zeigt (Besuch
  // aktiv) oder der Abruf gescheitert ist — sonst springt die Seite zu einer Position, die sich
  // gleich verschiebt. Danach genau einmal je Anker: spätere Änderungen (Flags treffen ein, ein
  // Abschnitt kommt hinzu) dürfen die Scrollposition des Nutzers nicht überschreiben. Fehlt der
  // Zielabschnitt noch (Flags laden), wird beim Erscheinen nachgeholt (`anzahl` in den Abhängigkeiten).
  const besuchBereit = besuch.status === 'aktiv' || news.isError;
  const gescrolltFuer = useRef<string | null>(null);
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) {
      gescrolltFuer.current = null;
      return;
    }
    if (anzahl === 0 || !besuchBereit || gescrolltFuer.current === id) return;
    const ziel = document.getElementById(id);
    if (!ziel) return;
    gescrolltFuer.current = id;
    ziel.scrollIntoView({ block: 'start' });
  }, [location.hash, besuchBereit, anzahl]);

  return (
    <div className="mx-auto max-w-3xl space-y-10 px-2 pb-12">
      <header className="space-y-3 pt-6">
        <h1
          className="font-medium leading-tight tracking-tight"
          style={{
            fontFamily: 'var(--f-display)',
            color: 'var(--ink)',
            fontSize: 'clamp(22px, 2.4vw, 32px)',
          }}
        >
          {t('hilfe.seite.titel')}
        </h1>
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('hilfe.seite.untertitel')}
        </p>
        <nav aria-label={t('hilfe.seite.inhalt')}>
          <ul className="flex flex-wrap gap-2">
            {themen.map((th) => (
              <li key={th.id}>
                <a
                  href={`#${th.anker}`}
                  className="inline-block rounded-full border px-3 py-1 text-xs hover:bg-[var(--surface-2)]"
                  style={{ borderColor: 'var(--line-soft)', color: 'var(--ink-2)' }}
                >
                  {t(th.titelKey)}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </header>
      <NewsBesuchContext.Provider value={besuch}>
        {themen.map(({ id, anker, titelKey, Komponente }) => (
          <section
            key={id}
            id={anker}
            aria-labelledby={`hilfe-${id}-titel`}
            className="scroll-mt-6 space-y-3"
          >
            <h2
              id={`hilfe-${id}-titel`}
              className="text-lg font-medium"
              style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}
            >
              {t(titelKey)}
            </h2>
            <Komponente />
          </section>
        ))}
      </NewsBesuchContext.Provider>
    </div>
  );
}
